import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Eye, Edit, Star, EyeOff, CheckCircle, XCircle, FileText, Calendar } from 'lucide-react'
import { fetchPlans } from '../services/api'
import Pagination from '../components/Pagination'
import CreatePlanModal from '../components/CreatePlanModal'

const PlansList = () => {
	const navigate = useNavigate()
	const [plans, setPlans] = useState([])
	const [isLoading, setIsLoading] = useState(true)
	const [error, setError] = useState('')
	const [currentPage, setCurrentPage] = useState(1)
	const [totalPages, setTotalPages] = useState(1)
	const [pageSize] = useState(10)
	const [showCreateModal, setShowCreateModal] = useState(false)

	const loadPlans = async (page = 1) => {
		setIsLoading(true)
		setError('')
		
		try {
			const response = await fetchPlans(page, pageSize)
			console.log('Plans response:', response)
			
			// Handle the new API response structure
			setPlans(response.content || [])
			setTotalPages(response.totalPages || 1)
			setCurrentPage(response.currentPage || page)
		} catch (err) {
			console.error('Error loading plans:', err)
			setError(err.message)
		} finally {
			setIsLoading(false)
		}
	}

	useEffect(() => {
		loadPlans(1)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const handlePageChange = (page) => {
		loadPlans(page)
	}

	const handleCreateSuccess = () => {
		setShowCreateModal(false)
		loadPlans(currentPage)
	}

	const formatDate = (dateString) => {
		if (!dateString) return 'N/A'
		return new Date(dateString).toLocaleDateString('pt-BR')
	}

	const handleViewDetails = (planId) => {
		navigate(`/plans/${planId}`)
	}

	if (error) {
		return (
			<div className="text-center py-12">
				<div className="text-red-600 text-lg font-medium mb-4">Error loading plans</div>
				<div className="text-gray-600 mb-4">{error}</div>
				<button
					onClick={() => loadPlans(currentPage)}
					className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
				>
					Try Again
				</button>
			</div>
		)
	}

	return (
		<div>
			<div className="sm:flex sm:items-center">
				<div className="sm:flex-auto">
					<h1 className="text-2xl font-semibold text-gray-900">Plans</h1>
					<p className="mt-2 text-sm text-gray-700">
						Manage digital account plans and their configurations.
					</p>
				</div>
				<div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
					<button
						type="button"
						onClick={() => setShowCreateModal(true)}
						className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:w-auto"
					>
						<Plus className="h-4 w-4 mr-2" />
						Create Plan
					</button>
				</div>
			</div>

			{isLoading ? (
				<div className="mt-8 bg-white shadow rounded-lg overflow-hidden">
					<div className="px-4 py-5 sm:p-6">
						<div className="animate-pulse">
							<div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
							<div className="space-y-3">
								{[...Array(5)].map((_, index) => (
									<div key={index} className="flex space-x-4">
										<div className="h-4 bg-gray-200 rounded w-1/6"></div>
										<div className="h-4 bg-gray-200 rounded w-1/4"></div>
										<div className="h-4 bg-gray-200 rounded w-1/6"></div>
										<div className="h-4 bg-gray-200 rounded w-1/6"></div>
										<div className="h-4 bg-gray-200 rounded w-1/6"></div>
										<div className="h-4 bg-gray-200 rounded w-1/6"></div>
									</div>
								))}
							</div>
						</div>
					</div>
				</div>
			) : plans.length === 0 ? (
				<div className="text-center py-12">
					<div className="text-gray-500 text-lg font-medium mb-4">No plans found</div>
					<div className="text-gray-400 mb-4">Get started by creating your first plan.</div>
					<button
						onClick={() => setShowCreateModal(true)}
						className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
					>
						<Plus className="h-4 w-4 mr-2" />
						Create Plan
					</button>
				</div>
			) : (
				<>
					<div className="mt-8 bg-white shadow rounded-lg overflow-hidden">
						<div className="px-4 py-5 sm:p-6">
							<div className="overflow-x-auto">
								<table className="min-w-full divide-y divide-gray-200">
									<thead className="bg-gray-50">
										<tr>
											<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
												Plan Details
											</th>
											<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
												Type
											</th>
											<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
												Status
											</th>
											<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
												Created
											</th>
											<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
												Contract
											</th>
											<th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
												Actions
											</th>
										</tr>
									</thead>
									<tbody className="bg-white divide-y divide-gray-200">
										{plans.map((plan) => (
											<tr key={plan.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleViewDetails(plan.id)}>
												<td className="px-6 py-4 whitespace-nowrap">
													<div className="flex items-center">
														<div className="flex-shrink-0">
															<div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
																<Eye className="h-5 w-5 text-primary-600" />
															</div>
														</div>
														<div className="ml-4">
															<div className="flex items-center gap-2">
																<div className="text-sm font-medium text-gray-900">
																	{plan.name}
																</div>
																{plan.planDefault && (
																	<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
																		<Star className="h-3 w-3 mr-1" />
																		Default
																	</span>
																)}
															</div>
															<div className="text-sm text-gray-500 max-w-xs truncate">
																{plan.description}
															</div>
															{plan.benefits && (
																<div className="text-xs text-gray-400 max-w-xs truncate mt-1">
																	{plan.benefits.length > 50 
																		? `${plan.benefits.substring(0, 50)}...` 
																		: plan.benefits
																	}
																</div>
															)}
														</div>
													</div>
												</td>
												<td className="px-6 py-4 whitespace-nowrap">
													<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
														{plan.type_account || 'N/A'}
													</span>
												</td>
												<td className="px-6 py-4 whitespace-nowrap">
													<div className="space-y-1">
														<span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
															plan.visibility 
																? 'bg-green-100 text-green-800' 
																: 'bg-red-100 text-red-800'
														}`}>
															{plan.visibility ? (
																<>
																	<Eye className="h-3 w-3 mr-1" />
																	Visible
																</>
															) : (
																<>
																	<EyeOff className="h-3 w-3 mr-1" />
																	Hidden
																</>
															)}
														</span>
														<div>
															<span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
																plan.availability 
																	? 'bg-green-100 text-green-800' 
																	: 'bg-red-100 text-red-800'
															}`}>
																{plan.availability ? (
																	<>
																		<CheckCircle className="h-3 w-3 mr-1" />
																		Available
																	</>
																) : (
																	<>
																		<XCircle className="h-3 w-3 mr-1" />
																		Unavailable
																	</>
																)}
															</span>
														</div>
													</div>
												</td>
												<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
													<div className="flex items-center">
														<Calendar className="h-4 w-4 mr-1" />
														{formatDate(plan.createdAt)}
													</div>
												</td>
												<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
													{plan.contract ? (
														<a 
															href={plan.contract} 
															target="_blank" 
															rel="noopener noreferrer"
															onClick={(e) => e.stopPropagation()}
															className="inline-flex items-center text-primary-600 hover:text-primary-800"
														>
															<FileText className="h-4 w-4 mr-1" />
															View
														</a>
													) : (
														<span className="text-gray-400">No contract</span>
													)}
												</td>
												<td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
													<button
														onClick={(e) => {
															e.stopPropagation()
															handleViewDetails(plan.id)
														}}
														className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
													>
														<Edit className="h-4 w-4 mr-1" />
														View Details
													</button>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
					</div>
					
					{totalPages > 1 && (
						<div className="mt-8">
							<Pagination
								currentPage={currentPage}
								totalPages={totalPages}
								onPageChange={handlePageChange}
							/>
						</div>
					)}
				</>
			)}

			{showCreateModal && (
				<CreatePlanModal
					onClose={() => setShowCreateModal(false)}
					onSuccess={handleCreateSuccess}
				/>
			)}
		</div>
	)
}

export default PlansList
